import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type {
  RegistryAssumption,
  RegistryIntervention,
  RegistryNonObjective,
  RegistrySignal,
  RegistryStage,
} from '../types';

export type CodeKind = 'assumption' | 'intervention' | 'signal' | 'non_objective' | 'stage';

type RegistryEntry =
  | RegistryAssumption
  | RegistryIntervention
  | RegistrySignal
  | RegistryNonObjective
  | RegistryStage;

interface CodeProps {
  id: string;
  kind: CodeKind;
  compact?: boolean;
  truncate?: boolean;
  /** Render only the code id (e.g. "S-01") with the label/detail on click — use
   *  where the human-readable name is already shown adjacent, to avoid repeating it. */
  codeOnly?: boolean;
}

const CODE_RE = /\b(A\d+[a-z]?|O-\d{2}|N\d+|M\d+|E\d+|S-\d{2})\b/g;
const POPOVER_MARGIN = 12;
const POPOVER_GAP = 6;

export function kindForCode(id: string): CodeKind | null {
  if (/^A\d+[a-z]?$/i.test(id)) return 'assumption';
  if (/^O-\d{2}$/i.test(id)) return 'intervention';
  if (/^S-\d{2}$/i.test(id)) return 'signal';
  if (/^N\d+$/i.test(id)) return 'non_objective';
  if (/^[ME]\d+$/i.test(id)) return 'stage';
  return null;
}

export function CodeText({ text, compact, codeOnly }: { text: string; compact?: boolean; codeOnly?: boolean }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(CODE_RE)) {
    const id = match[0];
    const index = match.index ?? 0;
    if (index > last) parts.push(text.slice(last, index));
    const kind = kindForCode(id);
    parts.push(kind ? <Code key={`${id}-${index}`} id={id} kind={kind} compact={compact} codeOnly={codeOnly} /> : id);
    last = index + id.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export default function Code({ id, kind, compact = false, truncate = false, codeOnly = false }: CodeProps) {
  const [open, setOpen] = useState(false);
  const [detailStyle, setDetailStyle] = useState<CSSProperties>({});
  const anchorRef = useRef<HTMLSpanElement>(null);
  const detailRef = useRef<HTMLSpanElement>(null);
  const registry = useQuery({
    queryKey: ['registry'],
    queryFn: api.registry,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const entry = useMemo(() => {
    const data = registry.data;
    if (!data) return null;
    const normalizedId = id.trim();
    const collection = {
      assumption: data.assumptions,
      intervention: data.interventions,
      signal: data.signals,
      non_objective: data.non_objectives,
      stage: data.stages,
    }[kind] as RegistryEntry[];
    return collection.find((item) => item.id.toLowerCase() === normalizedId.toLowerCase()) ?? null;
  }, [id, kind, registry.data]);

  useLayoutEffect(() => {
    if (!open) return;

    function updateDetailPosition() {
      const anchor = anchorRef.current;
      const detailNode = detailRef.current;
      if (!anchor || !detailNode) return;

      const anchorRect = anchor.getBoundingClientRect();
      const detailRect = detailNode.getBoundingClientRect();
      const cardRect = anchor.closest('.twin-slider .twincard')?.getBoundingClientRect();
      const availableWidth = Math.max(160, window.innerWidth - POPOVER_MARGIN * 2);
      const cardWidth = cardRect ? Math.max(160, cardRect.width - POPOVER_MARGIN * 2) : availableWidth;
      const maxWidth = Math.min(420, availableWidth, cardWidth);
      const minWidth = Math.min(cardRect ? 220 : 280, maxWidth);
      const detailWidth = Math.min(detailRect.width || minWidth, maxWidth);
      const detailHeight = detailRect.height || 90;

      let left = anchorRect.left;
      if (cardRect) {
        left = Math.max(
          cardRect.left + POPOVER_MARGIN,
          Math.min(left, cardRect.right - detailWidth - POPOVER_MARGIN),
        );
      } else {
        left = Math.max(POPOVER_MARGIN, Math.min(left, window.innerWidth - detailWidth - POPOVER_MARGIN));
      }

      let top = anchorRect.bottom + POPOVER_GAP;
      const overflowsCard = cardRect && top + detailHeight > cardRect.bottom - POPOVER_MARGIN;
      if (overflowsCard || top + detailHeight + POPOVER_MARGIN > window.innerHeight) {
        const above = anchorRect.top - detailHeight - POPOVER_GAP;
        const minTop = cardRect ? cardRect.top + POPOVER_MARGIN : POPOVER_MARGIN;
        top = above >= minTop
          ? above
          : Math.max(POPOVER_MARGIN, window.innerHeight - detailHeight - POPOVER_MARGIN);
      }

      setDetailStyle({
        left,
        top,
        minWidth,
        maxWidth,
        width: cardRect ? maxWidth : undefined,
      });
    }

    updateDetailPosition();
    window.addEventListener('resize', updateDetailPosition);
    window.addEventListener('scroll', updateDetailPosition, true);

    return () => {
      window.removeEventListener('resize', updateDetailPosition);
      window.removeEventListener('scroll', updateDetailPosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function closeOnPointerDown(event: PointerEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (anchorRef.current?.contains(target) || detailRef.current?.contains(target)) return;
      setOpen(false);
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('pointerdown', closeOnPointerDown, true);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('pointerdown', closeOnPointerDown, true);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  if (!entry) {
    return <span className="mono code-fallback">{id}</span>;
  }

  const label = labelFor(kind, entry);
  const detail = detailFor(kind, entry);

  return (
    <span
      ref={anchorRef}
      className={`code-ref${compact ? ' compact' : ''}${truncate ? ' truncate' : ''}${codeOnly ? ' code-only' : ''}${open ? ' open' : ''}`}
      tabIndex={0}
      role="button"
      aria-expanded={open}
      title={`${label} · ${entry.id} — click for detail`}
      onBlur={() => setOpen(false)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen((v) => !v);
        } else if (e.key === 'Escape') {
          setOpen(false);
        }
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen((v) => !v);
      }}
    >
      {!codeOnly && <span className="code-label">{label}</span>}
      <span className="code-id">{entry.id}</span>
      {open && (
        createPortal(
          <span
            ref={detailRef}
            className="code-detail"
            role="tooltip"
            style={detailStyle}
            onClick={(e) => e.stopPropagation()}
          >
            {detail}
          </span>,
          document.body,
        )
      )}
    </span>
  );
}

function labelFor(kind: CodeKind, entry: RegistryEntry): string {
  if (kind === 'stage') return (entry as RegistryStage).name;
  return (entry as Exclude<RegistryEntry, RegistryStage>).label;
}

function detailFor(kind: CodeKind, entry: RegistryEntry): React.ReactNode {
  if (kind === 'assumption') {
    const a = entry as RegistryAssumption;
    return (
      <>
        <b>{a.full_text}</b>
        <span>Owner: {a.owner}</span>
        <span>Tripwire: {a.tripwire}</span>
      </>
    );
  }
  if (kind === 'intervention') {
    const i = entry as RegistryIntervention;
    return (
      <>
        <b>{stripTierToken(i.what)}</b>
        <span>Lever: {i.lever}</span>
        <span>Owner: {i.owner}</span>
        <span>Class: {i.modality_class}</span>
      </>
    );
  }
  if (kind === 'signal') {
    const s = entry as RegistrySignal;
    return (
      <>
        <b>{s.label}</b>
        <span>Source: {s.source_ref}</span>
        <span>Class: {s.class}</span>
      </>
    );
  }
  if (kind === 'non_objective') {
    const n = entry as RegistryNonObjective;
    return (
      <>
        <b>{n.label}</b>
        <span>Rule: {n.rule}</span>
      </>
    );
  }
  const stage = entry as RegistryStage;
  return (
    <>
      <b>{stage.name}</b>
      <span>Engine {stage.engine_no}</span>
    </>
  );
}

function stripTierToken(value: string): string {
  return value.replace(/^\s*(LIGHTHOUSE|FLAGSHIP|CORE)\.?\s+/i, '').trim();
}
